import { createSelector } from 'reselect';
import AppsApi from './AppsApi';
import { getEntities } from '../../EntityData';
import { appSchema, ENTITY_APPS } from '../../../Schema/Apps';
import { EmptyObject } from '../../EmptyDefaults';
import { isPresent } from 'utils/isPresent';

const getStatuses = (state) => state.statuses;

export const getApps = createSelector(getEntities, (entities) => {
  if (isPresent(entities) && isPresent(entities[ENTITY_APPS])) {
    return entities[ENTITY_APPS];
  }
  return EmptyObject;
});

export const getApp = (assetId) => createSelector(getApps, (apps) => apps[assetId] || EmptyObject);

export const getAppStatus = (assetId) => createSelector(getApp(assetId), (app) => app.status || EmptyObject);

export const getAppName = createSelector(
  (state, assetId) => getAppStatus(assetId)(state),
  (appStatus) => appStatus.app || ''
);

export const getCanUseApp = (assetId) =>
  createSelector(getAppStatus(assetId), (appStatus) => appStatus.can_use_feature || false);

export const getAppEnabled = (assetId) =>
  createSelector(getAppStatus(assetId), (appStatus) => appStatus.is_enabled || false);

export const getAppState = createSelector(
  (state, assetId) => getAppStatus(assetId)(state),
  (appStatus) => appStatus.state || 'disabled'
);

export const getFeaturedAppsStatuses = createSelector(getStatuses, (statuses) => {
  if (isPresent(statuses)) {
    return {
      chatOnYourWebsiteStatus: statuses.chat_on_your_website.is_enabled,
      vehicleGpsTrackingStatus: statuses.vehicle_gps_tracking.is_enabled,
      profitRhinoStatus: statuses.profit_rhino.is_enabled,
      dispatchStatus: statuses.dispatch.is_enabled,
      googleLSAStatus: statuses.google_reserve.is_enabled,
      wisetackCanUseFeature: statuses.wisetack.can_use_feature,
      wisetackStatus: statuses.wisetack.is_enabled,

    };
  }
  return EmptyObject;
});

export const getSalesAppsStatuses = createSelector(getStatuses, (statuses) => {
  if (isPresent(statuses)) {
    return {
      jobInboxStatus: statuses.job_inbox.is_enabled,
      salesProposalStatus: statuses.sales_proposal.is_enabled,
      onCallAirStatus: statuses.on_call_air.is_enabled,
      profitRhinoStatus: statuses.profit_rhino.is_enabled,
      responsiBidStatus: statuses.responsibid.is_enabled,
      serviceAgreementsStatus: statuses.service_agreements.is_enabled,
    };
  }
  return EmptyObject;
});

export const getLabsAppsStatuses = createSelector(getStatuses, (statuses) => {
  if (isPresent(statuses)) {
    return {
      beelineRoutesStatus: statuses.beeline_routes.is_enabled,
      signpostStatus: statuses.signpost.is_enabled,
      scheduleEngineStatus: statuses.schedule_engine.is_enabled,
      mojioStatus: statuses.mojio.is_enabled,
      websiteBuilderStatus: statuses.website_builder.is_enabled
    };
  }
  return EmptyObject;
});

export const getLabsAppsStates = createSelector(getStatuses, (statuses) => {
  if (isPresent(statuses)) {
    return {
      beelineRoutesState: statuses.beeline_routes.state,
      signpostState: statuses.signpost.state,
      scheduleEngineState: statuses.schedule_engine.state,
      mojioState: statuses.mojio.state,
    };
  }
  return EmptyObject;
});


export const getOperationAppsStatuses = createSelector(getStatuses, (statuses) => {
  if (isPresent(statuses)) {
    return {
      checklistsStatus: statuses.checklists.is_enabled,
      flatRatePricingStatus: statuses.flat_rate_pricing.is_enabled,
      jobCostStatus: statuses.job_cost.is_enabled,
      propertyProfileStatus: statuses.property_profile.is_enabled,
      timeTrackingStatus: statuses.time_tracking.is_enabled,
      vehicleGpsTrackingStatus: statuses.vehicle_gps_tracking.is_enabled,
      alexaStatus: statuses.alexa.is_enabled,
      companycamStatus: statuses.companycam.is_enabled,
      googleCalendarStatus: statuses.google_calendar.is_enabled,
      measurequickStatus: statuses.measurequick.is_enabled,
      qboStatus: statuses.qbo.is_enabled && statuses.qbo.can_use_feature,
      quickbooksDesktopStatus: statuses.quickbooks_desktop.is_enabled,
      webhooksStatus: statuses.webhooks.is_enabled,
      zapierStatus: statuses.zapier.is_enabled,
      eCommerceStatus: statuses.e_commerce.is_enabled,
      apiKeyManagementStatus: statuses.api_key_management?.is_enabled
    };
  }
  return EmptyObject;
});

export const getHousecallApiKey = createSelector(
  (state, assetId) => getAppStatus(assetId)(state),
  (appStatus) => appStatus.housecall_api_key || ''
);

export const fetchAppStatus = (assetId) => ({
  fetchingApiFunc: AppsApi.getStatus,
  selector: getAppStatus(assetId),
  paramsFromProps: () => [assetId],
  schema: appSchema,
});
